import React, { useEffect, useState } from 'react';
import './signup.css';
import logo from '../../assets/images/logo.png';

import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import ApiServices from '../../services/apiServices';

function Signup() {
  const navigate = useNavigate();

  const [signupMode, setSignupMode] = useState('email');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (!resendSeconds) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((seconds) => Math.max(seconds - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSeconds]);

  const resendLabel = () => {
    const minutes = Math.floor(resendSeconds / 60);
    const seconds = String(resendSeconds % 60).padStart(2, '0');
    return `Resend OTP in ${minutes}:${seconds}`;
  };

  const validate = () => {
    if (!form.fullName.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return 'Invalid email format';

    if (form.password.length < 6)
      return 'Password must be at least 6 characters';

    if (form.password !== form.confirmPassword)
      return 'Passwords do not match';

    return null;
  };

  const checkEmailExists = async (email) => {
    const methods = await firebase
      .auth()
      .fetchSignInMethodsForEmail(email);
    return methods.length > 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      const exists = await checkEmailExists(form.email);
      if (exists) {
        setError('Email already exists. Please sign in.');
        return;
      }

      const userCredential = await firebase
        .auth()
        .createUserWithEmailAndPassword(form.email, form.password);

      const user = userCredential.user;

      await user.updateProfile({
        displayName: form.fullName
      });

      const userInfo = {
        uid: user.uid,
        email: user.email,
        displayName: form.fullName,
        photoURL: user.photoURL || ''
      };

      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(userInfo),
        process.env.REACT_APP_SECERET_KEY
      ).toString();

      localStorage.setItem('userKey', encrypted);
      localStorage.removeItem('activeProfile');
      localStorage.removeItem('activeProfileId');

      // ✅ FIX: reload + navigate correctly
      localStorage.setItem('profileSelectionRequired', '1');
      window.location.replace('/');

    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMobileOtp = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await ApiServices.callPostAPI('auth/mobile/send-otp', { mobile, purpose: 'signup' });
      if (response.data?.valid) {
        setOtpSent(true);
        setResendSeconds(120);
        return true;
      }
      setError(response.data?.error || 'Unable to send OTP');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to send OTP');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleResendMobileOtp = async () => {
    setResending(true);
    await handleSendMobileOtp();
    setResending(false);
  };

  const handleVerifyMobileOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await ApiServices.callPostAPI('auth/mobile/verify-otp', { mobile, otp, purpose: 'signup' });
      const customToken = response.data?.data?.customToken;
      if (!response.data?.valid || !customToken) {
        setError(response.data?.error || 'Invalid OTP');
        return;
      }
      const result = await firebase.auth().signInWithCustomToken(customToken);
      if (result.user) {
        localStorage.setItem('usermail', response.data.data.mobile || result.user.uid || '');
        localStorage.removeItem('activeProfile');
        localStorage.removeItem('activeProfileId');
        localStorage.setItem('profileSelectionRequired', '1');
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Mobile signup failed. Please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-main flex justify-content-center">
      <div className="login-main-inner signup-box">
        <img src={logo} className="App-logo" alt="logo" />

        <h3>Create your account</h3>

        <div className="login-tabs">
          <button
            type="button"
            className={signupMode === 'email' ? 'active' : ''}
            onClick={() => setSignupMode('email')}
          >
            Email
          </button>
          <button
            type="button"
            className={signupMode === 'mobile' ? 'active' : ''}
            onClick={() => setSignupMode('mobile')}
          >
            Mobile
          </button>
        </div>

        {signupMode === 'email' ? (
          <form onSubmit={handleSignup} className="signup-form">
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={form.fullName}
              onChange={handleChange}
              required
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              required
            />

            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                required
              />
              <span
                className="eye-icon"
                style={{ color: '#ff03c1', cursor: 'pointer' }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </span>
            </div>

            <div className="password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm Password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
              <span
                className="eye-icon"
                style={{ color: '#ff03c1', cursor: 'pointer' }}
                onClick={() =>
                  setShowConfirmPassword(!showConfirmPassword)
                }
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </span>
            </div>

            {error && <div className="error-text">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{ backgroundColor: '#ff03c1' }}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={otpSent ? handleVerifyMobileOtp : handleSendMobileOtp}
            className="signup-form"
          >
            <input
              type="tel"
              placeholder="Mobile number"
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value);
                setOtpSent(false);
                setResendSeconds(0);
                setOtp('');
              }}
              required
            />
            {otpSent && (
              <div>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength="6"
                  required
                />
                <button
                  type="button"
                  className="resend-otp"
                  onClick={handleResendMobileOtp}
                  disabled={loading || resending || resendSeconds > 0}
                >
                  {resending ? 'Resending...' : (resendSeconds > 0 ? resendLabel() : 'Resend OTP')}
                </button>
              </div>
            )}

            {error && <div className="error-text">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{ backgroundColor: '#ff03c1' }}
            >
              {loading ? 'Please wait...' : (otpSent ? 'Verify OTP' : 'Send OTP')}
            </button>
          </form>
        )}

        <div className="switch-auth">
          Already have an account?{' '}
          <span
            onClick={() => navigate('/login')}
            style={{ color: '#ff03c1', cursor: 'pointer' }}
          >
            Sign in
          </span>
        </div>
      </div>
    </div>
  );
}

export default Signup;
